import { useEffect, useState } from "react";
import { db } from "../../firebase/firestore";
import { collection, doc, getDocs, onSnapshot } from "firebase/firestore";

// COMPONENTS
import Navbar from "./components/Navbar";
import HeroBanner from "./components/HeroBanner";
import CategoryFilter from "./components/CategoryFilter";
import FoodCard from "./components/FoodCard";
import Footer from "./components/Footer";

import { logError, logSuccess, getErrorMessage } from "../../utils/errorHandler";

export default function Home() {
  const [items, setItems] = useState([]);
  const [banners, setBanners] = useState([]);
  const [category, setCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [platformConfig, setPlatformConfig] = useState({
    websiteClosed: false,
    websiteCloseMessage: "",
    acceptOrders: true,
  });

  // 🔥 OPTIMIZATION & ERROR HANDLING
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // 🔥 PAGINATION
  const [visibleCount, setVisibleCount] = useState(8);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError("");
        logSuccess("Home", "Fetching items and banners...");
        
        const [itemSnap, bannerSnap] = await Promise.all([
          getDocs(collection(db, "items")),
          getDocs(collection(db, "banners"))
        ]);

        const itemData = itemSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const bannerData = bannerSnap.docs.map(d => d.data());

        setItems(itemData);
        setBanners(bannerData);
        logSuccess("Home", `Loaded ${itemData.length} items and ${bannerData.length} banners`);
        setLoading(false);
      } catch (err) {
        logError("Home Fetch Data", err);
        setError(getErrorMessage(err));
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    try {
      const unsub = onSnapshot(
        doc(db, "appConfig", "platform"),
        (snap) => {
          try {
            if (!snap.exists()) {
              logSuccess("Home Config", "Using default platform config");
              return;
            }
            setPlatformConfig((prev) => ({ ...prev, ...snap.data() }));
            setError("");
            logSuccess("Home Config", "Platform config loaded");
          } catch (err) {
            logError("Home Config Snapshot", err);
            setError(getErrorMessage(err));
          }
        },
        (error) => {
          logError("Home Config Listener", error);
          // Non-critical error - keep defaults
        }
      );

      return () => unsub();
    } catch (err) {
      logError("Home Config Setup", err);
      setError(getErrorMessage(err));
    }
  }, []);

  // 🔥 LISTEN FOR SEARCH EVENT
  useEffect(() => {
    const handleSearch = (event) => {
      setSearchQuery(event.detail.toLowerCase());
      setVisibleCount(8);
      setCategory("all");
    };

    window.addEventListener("search-items", handleSearch);
    return () => window.removeEventListener("search-items", handleSearch);
  }, []);

  // 🔥 FILTER
  let filteredItems = items.filter(item => {
    const matchesCategory = category === "all" || item.category === category;
    const matchesSearch = !searchQuery || 
      item.name?.toLowerCase().includes(searchQuery) ||
      item.description?.toLowerCase().includes(searchQuery) ||
      item.category?.toLowerCase().includes(searchQuery);
    return matchesCategory && matchesSearch;
  });

  const visibleItems = filteredItems.slice(0, visibleCount);

  return (
    <div className="home">

      <Navbar />

      {/* 🔥 ERROR DISPLAY */}
      {error && (
        <div className="error-banner">
          ⚠️ {error}
        </div>
      )}

      {/* 🔥 LOADING STATE */}
      {loading && (
        <div className="notice">
          ⏳ Loading menu items...
        </div>
      )}

      {/* 🔥 NOTICE */}
      <div className="notice">
        ⚡ Before <b>10 PM</b>: delivery next day | After <b>10 PM</b>: delivery in 3 days | Pre-schedule up to 1 week
      </div>

      {(platformConfig.websiteClosed || platformConfig.acceptOrders === false) && (
        <div className="search-result-banner">
          {platformConfig.websiteCloseMessage || "We are temporarily closed and not accepting new orders right now."}
        </div>
      )}

      {searchQuery && (
        <div className="search-result-banner">
          Showing results for "<b>{searchQuery}</b>" ({filteredItems.length} items found)
          <button className="clear-search" onClick={() => setSearchQuery("")}>✕ Clear</button>
        </div>
      )}

      <HeroBanner banners={banners} />

      <CategoryFilter category={category} setCategory={setCategory} />

      {/* 🔥 ITEMS GRID */}
      <div className="items-grid">
        {loading ? (
          <div className="no-items-found">
            <p>Loading menu...</p>
          </div>
        ) : visibleItems.length > 0 ? (
          visibleItems.map(item => (
            <FoodCard key={item.id} item={item} />
          ))
        ) : (
          <div className="no-items-found">
            <p>No items found {searchQuery ? `for "${searchQuery}"` : ""}</p>
            <button onClick={() => setSearchQuery("")}>Clear Search</button>
          </div>
        )}
      </div>

      {/* 🔥 LOAD MORE */}
      {visibleCount < filteredItems.length && (
        <div className="load-more">
          <button onClick={() => setVisibleCount(prev => prev + 8)}>
            Load More
          </button>
        </div>
      )}

      <Footer />

    </div>
  );
}